import {ChangeDetectorRef, Component, DestroyRef, inject, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin, Observable, pipe} from 'rxjs';
import {debounceTime, filter, map, switchMap, take} from 'rxjs/operators';
import {DeliveryServiceBasicDto, DeliveryTypeDto} from '|api/codebook';
import {
  ConsignmentType,
  DistributionClass,
  DocumentState,
  EmpowermentDto,
  InternalNotificationKey,
  NodeType
} from '|api/commons';
import {ApiAuthorizationService, AuthorizedEntityType, ReceivedConsignmentAuthorizedOperation} from '|api/core';
import {DocumentDto, ReceivedDocumentDto} from '|api/document';
import {
  ApiReceivedDigitalConsignmentService,
  ApiReceivedPaperConsignmentService,
  DistributionNodeType,
  ReceivedDigitalConsignmentDto,
  ReceivedInternalDigitalConsignmentDto,
  ReceivedInternalPaperConsignmentDto,
  ReceivedPaperConsignmentDto,
  ReceivedPortalConsignmentDto,
  ReceivedTelefaxConsignmentDto,
} from '|api/sad';
import {
  AddressCompleteDto,
  addressDtoToForm,
  AddressPipe,
  anonymousSenderSet,
  CodebookService,
  CONSIGNMENT_WITH_DUPLICATE_RESOLVE_SERVICE,
  createAddressFormGroup,
  DocumentDetailCountType,
  DocumentDetailService,
  EmpowermentPipe,
  EsslComponentLinkComponent,
  FORM_SUBMIT_DEBOUNCE_TIME,
  formatAddressForDto,
  getLastDigitalComponentVersionId,
  getSender,
  load,
  ObjectDetailPart,
  ObjectDetailSubmitActionType,
  openDataboxConsignmentValidationDialog,
  OrganizationalStructureService,
  OrganizationStructureType,
  OrgStructureSelectorComponent,
  PlainComponentVersionViewerDialogService,
  PlainComponentViewerMode,
  ReceiveConsignmentDisplaySenderComponent,
  ReceiveConsignmentSetSenderComponent,
  SharedBusinessValidators,
  SubjectAsSender,
  SubjectAttributeType,
  SubjectValidators
} from '|shared';
import {DocumentDetailComponent} from '../../document-detail.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiDigitalComponentService, DigitalComponentCompleteDto} from '|api/component';
import {
  AlertComponent,
  ButtonComponent,
  findDuplicates,
  IconComponent,
  LoadingIndicatorService,
  LocalizedBooleanPipe,
  processTimeControlWithMs,
  SectionComponent,
  stripMsFromTimeString,
  TestingFeatureDirective,
  TooltipDirective,
  WaitingDirective
} from '@icz/angular-essentials';
import {
  CheckboxComponent,
  DatePickerComponent,
  FormAutocompleteComponent,
  FormFieldComponent,
  FormTreeSelectorComponent,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczOption,
  IczValidators,
  TimePickerComponent,
  TreeItemSelectionStrategy,
  ValidationErrorsListComponent
} from '@icz/angular-form-elements';
import {Page} from '@icz/angular-table';
import {ReactiveFormsModule} from '@angular/forms';
import {DeliveryInfoEmailComponent} from './delivery-info-email/delivery-info-email.component';
import {AsyncPipe} from '@angular/common';
import {CheckUnsavedFormPageService, IFormGroupCheckable} from '@icz/angular-application-core';
import {DialogService} from '@icz/angular-modal';
import {Router} from '@angular/router';
import {
  ApplicationRoute,
  createAbsoluteRoute,
  DocumentDetailRoute,
  DocumentsRoute,
  DocumentToastService,
  DocumentToastType,
  EsslHistoryService,
} from '|ui-essl-core';

type AbstractReceivedConsigmentDto = ReceivedDigitalConsignmentDto | ReceivedPaperConsignmentDto | ReceivedInternalPaperConsignmentDto | ReceivedInternalDigitalConsignmentDto | ReceivedTelefaxConsignmentDto;

function extractFirstItemFromPageIfAvailable<T>() {
  return pipe(
    filter((page: Page<T>) => !isNil(page.content) && page.content.length > 0),
    map(page => page.content![0]),
  );
}

@Component({
  selector: 'icz-document-detail-delivery',
  templateUrl: './document-detail-delivery.component.html',
  styleUrls: ['./document-detail-delivery.component.scss'],
  providers: [CheckUnsavedFormPageService],
  imports: [AlertComponent, WaitingDirective, IczFormContainerDirective, ReactiveFormsModule, SectionComponent, FormFieldComponent, FormAutocompleteComponent, DatePickerComponent, CheckboxComponent, IconComponent, TooltipDirective, TimePickerComponent, EsslComponentLinkComponent, DeliveryInfoEmailComponent, ReceiveConsignmentDisplaySenderComponent, ReceiveConsignmentSetSenderComponent, ValidationErrorsListComponent, ButtonComponent, AsyncPipe, LocalizedBooleanPipe, EmpowermentPipe, AddressPipe, OrgStructureSelectorComponent, FormTreeSelectorComponent, TestingFeatureDirective]
})
export class DocumentDetailDeliveryComponent implements OnInit, IFormGroupCheckable {

  protected loadingService = inject(LoadingIndicatorService);
  private documentDetailService = inject(DocumentDetailService);
  private codebookService = inject(CodebookService);
  private historyService = inject(EsslHistoryService);
  private apiReceivedDigitalConsignmentService = inject(ApiReceivedDigitalConsignmentService);
  private apiReceivedPaperConsignmentService = inject(ApiReceivedPaperConsignmentService);
  private consignmentWithDuplicateResolveService = inject(CONSIGNMENT_WITH_DUPLICATE_RESOLVE_SERVICE);
  private documentToastService = inject(DocumentToastService);
  private translateService = inject(TranslateService);
  private organizationalStructureService = inject(OrganizationalStructureService);
  private apiAuthorizationService = inject(ApiAuthorizationService);
  private apiDigitalComponentService = inject(ApiDigitalComponentService);
  private destroyRef = inject(DestroyRef);
  private cd = inject(ChangeDetectorRef);
  protected plainComponentVersionViewerDialogService = inject(PlainComponentVersionViewerDialogService);
  protected checkUnsavedService = inject(CheckUnsavedFormPageService);
  private dialogService = inject(DialogService);
  private router = inject(Router);
  private parentComponent = inject(DocumentDetailComponent, {optional: true});

  formGroupsToCheck!: string[];
  sheetNodesOptions: IczOption[] = [];

  receivedPaperConsignment$: Observable<ReceivedPaperConsignmentDto> = this.documentDetailService.object$.pipe(
    switchMap(document =>
      this.apiReceivedPaperConsignmentService
        .receivedPaperConsignmentFindConsignmentsForDocument({
          id: document!.id,
          body: {
            page: 0,
          }
        }) as Observable<Page<ReceivedPaperConsignmentDto>>
    ),
    extractFirstItemFromPageIfAvailable(),
  );

  receivedDigitalConsignmentWithSourceMessage$: Observable<ReceivedDigitalConsignmentDto> = this.documentDetailService.object$.pipe(
    switchMap(document =>
      this.apiReceivedDigitalConsignmentService
        .receivedDigitalConsignmentFindConsignmentsForDocument({
          id: document!.id,
          body: {
            page: 0,
          }
        }) as Observable<Page<ReceivedDigitalConsignmentDto>>
    ),
    extractFirstItemFromPageIfAvailable(),
  );

  receivedInternalDigitalConsignment$: Observable<ReceivedInternalDigitalConsignmentDto> = this.documentDetailService.object$.pipe(
    switchMap(document =>
      this.apiReceivedDigitalConsignmentService
        .receivedDigitalConsignmentFindInternalConsignmentsForDocument({
          id: document!.id,
          body: {
            page: 0,
          }
        }) as Observable<Page<ReceivedInternalDigitalConsignmentDto>>
    ),
    extractFirstItemFromPageIfAvailable(),
  );

  receivedInternalPaperConsignment$: Observable<ReceivedInternalPaperConsignmentDto> = this.documentDetailService.object$.pipe(
    switchMap(document =>
      this.apiReceivedPaperConsignmentService
        .receivedPaperConsignmentFindInternalConsignmentsForDocument({
          id: document!.id,
          body: {
            page: 0,
          }
        }) as Observable<Page<ReceivedInternalPaperConsignmentDto>>
    ),
    extractFirstItemFromPageIfAvailable(),
  );

  receivedFaxConsignment$: Observable<ReceivedTelefaxConsignmentDto> = this.documentDetailService.object$.pipe(
    switchMap(document =>
      this.apiReceivedPaperConsignmentService
        .receivedPaperConsignmentFindTelefaxConsignmentsForDocument({
          id: document!.id,
          body: {
            page: 0,
          }
        }) as Observable<Page<ReceivedTelefaxConsignmentDto>>
    ),
    extractFirstItemFromPageIfAvailable(),
  );

  receivedPortalConsignment$: Observable<ReceivedPortalConsignmentDto> = this.documentDetailService.object$.pipe(
    switchMap(document =>
      this.apiReceivedDigitalConsignmentService
        .receivedDigitalConsignmentFindPortalConsignmentsForDocument({
          id: document!.id,
          body: {
            page: 0,
          }
        }) as Observable<Page<ReceivedTelefaxConsignmentDto>>
    ),
    extractFirstItemFromPageIfAvailable(),
  );

  form = new IczFormGroup({
    uid: new IczFormGroup({
      uid: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    }),
    // basic consignment info and data for paper consignment
    auditInfo: new IczFormGroup({
      createdAt: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
      createdByFunctionalPositionId: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
      modifiedAt: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    }),
    contactPerson: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    deliveryDate: new IczFormControl<Nullable<string>>({value: null, disabled: true}, [IczValidators.required()]),
    deliveryTime: new IczFormControl<Nullable<string>>({value: null, disabled: true}, [IczValidators.isTime()]),
    deliveryType: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    dispatchDate: new IczFormControl<Nullable<string>>(null, [SharedBusinessValidators.isDispatchDateValid()]),
    dispatchTime: new IczFormControl<Nullable<string>>(null, [IczValidators.isTime()]),
    basicServiceId: new IczFormControl<Nullable<number>>(null),
    exclusivePersonalDelivery: new IczFormControl<Nullable<boolean>>(false),
    keepEnvelope: new IczFormControl<Nullable<boolean>>(false),
    label: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    noOfficialContent: new IczFormControl<Nullable<boolean>>(false),
    note: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    personalDelivery: new IczFormControl<Nullable<boolean>>({value: false, disabled: true}),
    personalDeliveryUserName: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    postingNumber: new IczFormControl<Nullable<string>>({value: null, disabled: false}),
    recommended: new IczFormControl<Nullable<boolean>>({value: null, disabled: false}),
    recordedDelivery: new IczFormControl<Nullable<boolean>>(false),
    sender: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    senderAdditionalName: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    senderAddress: new IczFormControl<Nullable<AddressCompleteDto>>(null),
    senderAddressValid: new IczFormControl<Nullable<boolean>>(true, [IczValidators.required()]),
    // izc-address component formatted address
    senderAddressDto: createAddressFormGroup(),
    senderDefinition: new IczFormControl<Nullable<SubjectAsSender>>(null, [SubjectValidators.isValidSenderSubject()]),
    senderPostOffice: new IczFormControl<Nullable<string>>({value: null, disabled: false}),
    trackingNumber: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    // additional data for digital consignment
    consigneeOrganisationUnit: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    toHands: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    dataBoxDto: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    edificationNotification: new IczFormControl<Nullable<boolean>>({value: false, disabled: true}),
    senderEmailAddress: new IczFormControl<Nullable<string>>(null),
    empowerment: new IczFormControl<Nullable<EmpowermentDto>>({value: null, disabled: true}),
    senderOrganisationUnit: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    senderFileRefNumber: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    recipientRefNumber: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    recipientFileRefNumber: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    digitalComponentCount: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    nonPaperComponentTypeAndCount: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    externalId: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    subject: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    toOwnHands: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    senderInternalOrgUnit: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    senderInternalAddress: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    consignorRefNumber: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    consignorFileRefNumber: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    originalSender: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    filingOfficeSheetNodeId: new IczFormControl<Nullable<number>>(null),
    addresseeFunctionalPositionId: new IczFormControl<Nullable<string>>(null, []),
    addresseeOrgUnitId: new IczFormControl<Nullable<number>>(null, []),
    // portal consignment information
    targetDistributionNodeId: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    anonymousSender: new IczFormControl<Nullable<boolean>>(false, []),
    anonymousSenderFirstName: new IczFormControl<Nullable<string>>(null, []),
    anonymousSenderSurname: new IczFormControl<Nullable<string>>(null, [])
  });

  receivedDigitalConsignment!: ReceivedDigitalConsignmentDto;
  receivedPaperConsignment!: ReceivedPaperConsignmentDto;
  receivedDigitalInternalConsignment: Nullable<ReceivedInternalDigitalConsignmentDto>;
  receivedPaperInternalConsignment: Nullable<ReceivedInternalPaperConsignmentDto>;
  receivedTelefaxConsignment: Nullable<ReceivedTelefaxConsignmentDto>;
  receivedPortalConsignment: Nullable<ReceivedPortalConsignmentDto>;

  consignmentDistributionClass: Nullable<DistributionClass>;
  deliveryTypeName: Nullable<string> = null;
  originalDispatchTime: Nullable<string> = null;
  originalDeliveryTime: Nullable<string> = null;
  digitalConsignmentDefectsCount = 0;

  isPaperConsignment: boolean = false;
  isDigitalConsignment: boolean = false;
  isPaperInternalConsignment: boolean = false;
  isDigitalInternalConsignment: boolean = false;
  isTelefaxConsignment = false;
  isPortalConsignment = false;
  hasAnalogReceivedComponents = false;
  hasDigitalReceivedComponents = false;

  personalConsignmentVerificationProtocol: Nullable<DigitalComponentCompleteDto>;
  envelopeComponent: Nullable<DigitalComponentCompleteDto>;

  deliveryTypeOptions!: DeliveryTypeDto[];
  portalDistributionNodeOptions: IczOption[] = [];

  readonly SubjectAttributeType = SubjectAttributeType;
  timepickerDisabled = true;
  senderSubjectChanged = false;
  private hideSubmitBarTimeout: Nullable<number>;

  functionalPositionOptions$: Observable<IczOption[]> = this.organizationalStructureService.functionalPositionsOptions();
  functionalPositionOptionsWithId$: Observable<IczOption[]> = this.organizationalStructureService.functionalPositionTreeOptionsWithId();

  currentDocument!: DocumentDto;

  wasSenderRemoved = false;

  isDisplayProfileDenied = false;
  isDisplayProfileAllowed = false;
  canModifyContent = false;

  basicServiceWithDistributorOptions: IczOption[] = [];
  basicServices: DeliveryServiceBasicDto[] = [];

  defectsAlertHeading = 'Zpráva obsahovala komponenty s vadou ({{count}})';

  get consignment() {
    return this.receivedPaperConsignment || this.receivedDigitalConsignment || this.receivedTelefaxConsignment || this.receivedPaperInternalConsignment || this.receivedDigitalInternalConsignment || this.receivedPortalConsignment;
  }

  get isPortalOrDataboxDelivery() {
    return this.isPortalDelivery || this.isDataBoxDelivery;
  }

  get isDigitalDelivery() {
    return this.isEmailDelivery || this.isPortalOrDataboxDelivery;
  }

  get isEmailDelivery() {
    return this.consignmentDistributionClass === DistributionClass.EMAIL_BOX;
  }

  get isOnPlaceDeliveryType() {
    return this.consignmentDistributionClass === DistributionClass.ON_PLACE;
  }

  get isPostDelivery() {
    return (this.consignmentDistributionClass === DistributionClass.ADDRESS_PLACE || this.consignmentDistributionClass === DistributionClass.PHYSICAL_BOX);
  }

  get isDataBoxDelivery() {
    return this.consignmentDistributionClass === DistributionClass.ISDS_BOX;
  }

  get isPortalDelivery() {
    return this.consignmentDistributionClass === DistributionClass.PORTAL_BOX;
  }

  get isInternalDelivery() {
    return this.consignmentDistributionClass === DistributionClass.INTERNAL;
  }

  get senderDefinitionControl(): IczFormControl {
    return this.form?.get('senderDefinition') as IczFormControl;
  }

  get senderAddress() {
    return this.form.get('senderAddressDto') as IczFormGroup;
  }

  get personalDelivery() {
    return this.form?.get('personalDelivery')!.value;
  }

  get empowermentValue() {
    return this.form?.get('empowerment')!.value;
  }

  get isDocumentDeactivated() {
    return this.currentDocument ? this.currentDocument.documentState === DocumentState.DEACTIVATED : false;
  }

  get isDocumentSettled() {
    return this.currentDocument ? this.currentDocument.documentState === DocumentState.SETTLED : false;
  }

  get isFormDisabledByDocumentState() {
    return this.isDocumentDeactivated || this.isDocumentSettled;
  }

  get senderInternalAddress() {
    if (this.isPaperInternalConsignment) {
      return this.receivedPaperInternalConsignment!.senderInternalAddress;
    } else if (this.isDigitalInternalConsignment) {
      return this.receivedDigitalInternalConsignment!.senderInternalAddress;
    }
    return null;
  }

  get senderOU() {
    return this.form.get('senderInternalOrgUnit')!.value;
  }

  get isConsignmentRecommended() {
    return Boolean(this.form.get('recommended')!.value);
  }

  get deliveryTimeValue() {
    return this.form.get('deliveryTime')!.value;
  }

  get isDeliveryTimeDisabled() {
    return this.form.get('deliveryTime')!.disabled;
  }

  get dispatchTimeValue() {
    return this.form.get('dispatchTime')!.value;
  }

  get isDispatchTimeDisabled() {
    return this.form.get('dispatchTime')!.disabled;
  }

  get hasHeaderDataboxMessage() {
    return this.receivedDigitalConsignment && this.receivedDigitalConsignment.headerDataboxMessageDigitalComponentVersionId;
  }

  get isPostOrOnPlaceDelivery() {
    return this.isPostDelivery || this.consignmentDistributionClass === DistributionClass.ON_PLACE;
  }

  get deliveryTimeTooltip() {
    if (this.isPostOrOnPlaceDelivery) {
      return 'ui.document.datetime.tooltip';
    } else if (this.receivedDigitalConsignment && this.receivedDigitalConsignment.dataBoxDto) {
      return 'ui.document.deliveryTimeOvm.tooltip';
    } else {
      return null;
    }
  }

  get formattedPaperComponentsCount(): string {
    let composition: string = '';
    const compositionSeparator = ' + ';
    if (this.currentDocument) {
      if (isNil(this.currentDocument.receivedQuantification?.mainPaperComponentSheetCount)) {
        composition += 0;
      } else {
        composition += String(this.currentDocument.receivedQuantification?.mainPaperComponentSheetCount);
      }

      if (isNil(this.currentDocument.receivedQuantification?.paperComponentSheetEnclosureCount)) {
        composition += `${compositionSeparator}${String(0)}/${String(0)}`;
      } else {
        const paperComponentSheetEnclosureCount = this.currentDocument.receivedQuantification?.paperComponentSheetEnclosureCount;
        const paperComponentSheetCount = this.currentDocument.receivedQuantification?.paperComponentSheetCount;
        composition += `${compositionSeparator}${String(paperComponentSheetEnclosureCount)}/${String(paperComponentSheetCount)}`;
      }

      if (isNil(this.currentDocument.receivedQuantification?.paperComponentVolumeEnclosureCount)) {
        composition += compositionSeparator + String(0) + ' ' + this.translateService.instant('Sv.');
      } else {
        const paperComponentVolumeEnclosureCount = this.currentDocument.receivedQuantification?.paperComponentVolumeEnclosureCount;
        composition += compositionSeparator + String(paperComponentVolumeEnclosureCount) + ' ' + this.translateService.instant('Sv.');
      }
    }
    return composition;
  }

  get digitalConsignmentHasProtocolAndOriginal() {
    return (
      this.receivedDigitalConsignment?.protocolDigitalComponentVersionId &&
      this.receivedDigitalConsignment?.originalDigitalComponentVersionId
    );
  }

  get paperConsignmentHasEnvelope() {
    return this.isPaperConsignment && !isNil(this.receivedPaperConsignment.envelopeComponentId) && this.envelopeComponent;
  }

  constructor() {
    this.checkUnsavedService.addUnsavedFormCheck(this, ['form']);
    this.documentDetailService.showSubmitBar = true;
    this.documentDetailService.form$.next(this.form);

    this.documentDetailService.object$.pipe(
      filter(Boolean),
      take(1)
    ).subscribe(document => {
      this.currentDocument = document as DocumentDto;
      this.form.get('nonPaperComponentTypeAndCount')!.setValue(this.currentDocument.receivedQuantification?.nonPaperComponentTypeAndCount);
      this.form.get('digitalComponentCount')!.setValue(this.currentDocument.receivedQuantification?.digitalComponentCount);
      this.hasDigitalReceivedComponents = (this.currentDocument.receivedQuantification?.digitalComponentCount! > 0);
      this.hasAnalogReceivedComponents = (this.currentDocument.receivedQuantification?.analogComponentCount! > 0);
      if (this.isFormDisabledByDocumentState) {
        this.form.disable();
      }
    });

    forkJoin([
      this.codebookService.deliveryTypes(),
      this.codebookService.deliveryServiceBasics(),
      this.codebookService.portalNodes(),
      this.codebookService.sheetNodes(),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([deliveryTypes, basicServices, portalNodes, sheetNodes]) => {
      this.deliveryTypeOptions = deliveryTypes.filter(type => type.nodeType === NodeType.LEAF);
      this.portalDistributionNodeOptions = portalNodes.map(node => ({value: node.id, label: node.nodeName}));
      this.basicServices = basicServices;
      this.sheetNodesOptions = sheetNodes.filter(node => node.purpose === DistributionNodeType.RECEIVE || node.purpose === DistributionNodeType.SEND_RECEIVE).map(node => ({value: node.id, label: node.nodeName}));
      this.loadConsignmentData();
    });
  }

  processConsignmentClassAndSender(consignment: AbstractReceivedConsigmentDto) {
    const consignmentDeliveryType = this.deliveryTypeOptions.filter(deliveryType => deliveryType.id === consignment.deliveryTypeId)[0];
    this.consignmentDistributionClass = consignmentDeliveryType.distributionClass?.distributionClassValue;

    this.basicServiceWithDistributorOptions =
      this.basicServices.filter(s => s.deliveryTypeId === consignment.deliveryTypeId)
        .map(s => ({value: s.id, label: `${s.name} - ${s.distributor.description}`}));
    this.deliveryTypeName = consignmentDeliveryType.name;
  }

  processConsignmentInfo(consignment: ReceivedPaperConsignmentDto | ReceivedDigitalConsignmentDto) {
    this.resolveAuthorizedOperations(consignment.id!);
    this.processConsignmentClassAndSender(consignment);
    this.form.patchValue(consignment as any);
    this.originalDispatchTime = consignment.dispatchTime; // nullable dispatch time with ms accuracy
    this.originalDeliveryTime = consignment.deliveryTime; // nullable delivery time with ms accuracy
    this.digitalConsignmentDefectsCount = findDuplicates(
      ((consignment as ReceivedDigitalConsignmentDto).sourceMessageDefects ?? []).flatMap(d => d.epdzComponentIds)
    ).length;

    if (this.isDigitalConsignment) this.form.get('label')!.setValue((consignment as ReceivedDigitalConsignmentDto).subject);
    this.form.get('deliveryTime')!.setValue(consignment.deliveryTime ? stripMsFromTimeString(consignment.deliveryTime) : null);
    if (this.isFormDisabledByDocumentState) {
      this.form.disable();
    } else if (this.isPostOrOnPlaceDelivery) {
      this.form.get('dispatchDate')?.enable();
      this.form.get('deliveryDate')?.enable();
      this.form.get('deliveryTime')?.enable();
      this.timepickerDisabled = false;
    } else if (this.isDigitalDelivery) {
      this.form.get('dispatchTime')!.setValue(consignment.dispatchTime ? stripMsFromTimeString(consignment.dispatchTime) : null);
      this.form.get('dispatchDate')!.setValidators([IczValidators.required()]);
      this.form.get('dispatchTime')!.setValidators([IczValidators.required()]);
    }
  }

  processInternalConsignmentInfo(consignment: ReceivedInternalPaperConsignmentDto | ReceivedInternalDigitalConsignmentDto) {
    this.resolveAuthorizedOperations(consignment.id!);
    this.processConsignmentClassAndSender(consignment);
    if (this.isFormDisabledByDocumentState) {
      this.form.disable();
    } else if (this.isPaperInternalConsignment) {
      this.form.get('deliveryDate')?.enable();
      this.form.get('deliveryTime')?.enable();
    }
    this.form.patchValue({
      uid: consignment.uid!,
      deliveryDate: consignment.deliveryDate,
      dispatchDate: consignment.dispatchDate,
      label: consignment.label,
      note: consignment.note,
      sender: consignment.sender,
      senderFileRefNumber: consignment.senderFileRefNumber,
      consignorRefNumber: consignment.consignorRefNumber,
      consignorFileRefNumber: consignment.consignorFileRefNumber,
      senderInternalOrgUnit: consignment.senderInternalOrgUnit,
      addresseeOrgUnitId: consignment.addresseeOrgUnitId,
      filingOfficeSheetNodeId: consignment.filingOfficeSheetNodeId
    });
    this.form.get('deliveryTime')!.setValue(consignment.deliveryTime ? stripMsFromTimeString(consignment.deliveryTime) : null);
    this.form.get('auditInfo')!.patchValue({
      createdAt: consignment.auditInfo?.createdAt,
      createdByFunctionalPositionId: consignment.auditInfo?.createdByFunctionalPositionId,
      modifiedAt: consignment.auditInfo?.modifiedAt,
    });
  }

  processFaxOrPortalConsignmentInfo(consignment: ReceivedTelefaxConsignmentDto | ReceivedPortalConsignmentDto) {
    this.resolveAuthorizedOperations(consignment.id!);
    this.processConsignmentClassAndSender(consignment);


    this.form.patchValue({
      uid: consignment.uid!,
      deliveryDate: consignment.deliveryDate,
      deliveryTime: consignment.deliveryTime,
      dispatchDate: consignment.dispatchDate,
      label: consignment.label,
      note: consignment.note,
      sender: consignment.sender,
      targetDistributionNodeId: consignment.targetDistributionNodeId,
    });

    const deliveryTimeControl = this.form.get('deliveryTime')!;
    deliveryTimeControl.disable();
    deliveryTimeControl.setValue(consignment.deliveryTime ? stripMsFromTimeString(consignment.deliveryTime) : null);
    this.form.get('dispatchTime')!.setValue(consignment.dispatchTime ? stripMsFromTimeString(consignment.dispatchTime) : null);

    this.form.get('auditInfo')!.patchValue({
      createdAt: consignment.auditInfo?.createdAt,
      createdByFunctionalPositionId: consignment.auditInfo?.createdByFunctionalPositionId,
      modifiedAt: consignment.auditInfo?.modifiedAt,
    });
  }

  ngOnInit() {
    this.documentDetailService.submit$.pipe(
      debounceTime(FORM_SUBMIT_DEBOUNCE_TIME),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(submitActionType => {
      this.onSubmit(submitActionType);
    });

    this.form.get('deliveryDate')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
      this.form.get('dispatchDate')!.updateValueAndValidity();
    });

    this.form.get('basicServiceId')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(basicServiceId => {
      if (basicServiceId) {
        const selectedService = this.basicServices.find(service => service.id === basicServiceId);
        if (selectedService) {
          this.form.get('recommended')!.setValue(selectedService.onlyDeliversAgainstConfirmation);
        }
      }
    });
  }

  private stripMsFromTimeConsignmentAttribute(attribute: Nullable<string>): Nullable<string> {
    if (attribute) {
      return stripMsFromTimeString(attribute);
    } else {
      return null;
    }
  }

  private loadConsignmentData() {
    this.documentDetailService.object$.pipe(
      take(1),
    ).subscribe(document => {
      const consignmentType = (document as ReceivedDocumentDto).consignmentType;

      if (consignmentType === ConsignmentType.RECEIVED_PAPER_ADDRESS) {
        this.receivedPaperConsignment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(paperConsignment => {
          this.isPaperConsignment = true;
          this.receivedPaperConsignment = paperConsignment;
          this.processConsignmentInfo(this.receivedPaperConsignment);
          const address = addressDtoToForm(this.receivedPaperConsignment.senderAddress as AddressCompleteDto);
          this.form.get('senderAddressDto')?.patchValue(address!);

          if (paperConsignment.verificationProtocolId) {
            this.apiDigitalComponentService.digitalComponentFindById({id: paperConsignment.verificationProtocolId}).subscribe(protocol => {
              this.personalConsignmentVerificationProtocol = protocol;
            });
          }

          if (paperConsignment.envelopeComponentId) {
            this.apiDigitalComponentService.digitalComponentFindById({id: paperConsignment.envelopeComponentId}).subscribe(envelope => {
              this.envelopeComponent = envelope;
            });
          }
        });
      }
      else if (consignmentType === ConsignmentType.RECEIVED_PAPER_INTERNAL) {
        this.receivedInternalPaperConsignment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(paperInternalConsignment => {
          this.isPaperInternalConsignment = true;
          this.receivedPaperInternalConsignment = paperInternalConsignment;
          this.processInternalConsignmentInfo(this.receivedPaperInternalConsignment);
        });
      }
      else if (consignmentType === ConsignmentType.RECEIVED_DIGITAL_INTERNAL) {
        this.receivedInternalDigitalConsignment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe( digitalInternalConsignment => {
          this.isDigitalInternalConsignment = true;
          this.receivedDigitalInternalConsignment = digitalInternalConsignment;
          this.processInternalConsignmentInfo(this.receivedDigitalInternalConsignment);
        });
      }
      else if (consignmentType === ConsignmentType.TELEFAX) {
        this.receivedFaxConsignment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe( telefaxConsignment => {
          this.isTelefaxConsignment = true;
          this.receivedTelefaxConsignment = telefaxConsignment;
          this.processFaxOrPortalConsignmentInfo(this.receivedTelefaxConsignment);
        });
      }
      else if (consignmentType === ConsignmentType.PORTAL) {
        this.receivedPortalConsignment$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(portalConsignment => {
          this.isPortalConsignment = true;
          this.receivedPortalConsignment = portalConsignment;
          this.processFaxOrPortalConsignmentInfo(this.receivedPortalConsignment);
        });
      }
      else if (consignmentType === ConsignmentType.RECEIVED_DIGITAL_EMAIL || consignmentType === ConsignmentType.RECEIVED_DIGITAL_DATABOX) {
        this.receivedDigitalConsignmentWithSourceMessage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(digitalConsignment => {
          this.isDigitalConsignment = true;
          this.receivedDigitalConsignment = digitalConsignment;
          this.processConsignmentInfo(this.receivedDigitalConsignment);
          const senderEmailAddressCtrl = this.form.get('senderEmailAddress')!;
          senderEmailAddressCtrl.setValue(digitalConsignment.emailAddressDto?.senderAddress);
          senderEmailAddressCtrl.setValidators([IczValidators.required(), IczValidators.email()]);
          senderEmailAddressCtrl.updateValueAndValidity();
        });
      }
    });
  }

  private resolveAuthorizedOperations(consignmentId: number) {
    this.apiAuthorizationService.authorizationAuthorizeReceivedConsignmentOperations({
      body: {
        operationsToAuthorize: [ReceivedConsignmentAuthorizedOperation.RECEIVED_CONSIGNMENT_SHOW_PROFILE, ReceivedConsignmentAuthorizedOperation.RECEIVED_CONSIGNMENT_MODIFY_PROFILE, ReceivedConsignmentAuthorizedOperation.RECEIVED_CONSIGNMENT_SHOW_CONTENT],
        authorizedEntityId: consignmentId,
        authorizedEntityType: AuthorizedEntityType.RECEIVED_CONSIGNMENT,
      }
    }).subscribe(result => {
      if (result) {
        this.isDisplayProfileAllowed = result.authorizedOperations.find(op => op.operation === ReceivedConsignmentAuthorizedOperation.RECEIVED_CONSIGNMENT_SHOW_PROFILE)!.userAuthorized;
        this.isDisplayProfileDenied = !this.isDisplayProfileAllowed;
        this.canModifyContent = result.authorizedOperations.find(op => op.operation === ReceivedConsignmentAuthorizedOperation.RECEIVED_CONSIGNMENT_MODIFY_PROFILE)!.userAuthorized;
        if (!this.canModifyContent) {
          this.form.disable();
        }
        this.cd.markForCheck();
      }
    });
  }

  setSender(subject: SubjectAsSender) {
    this.senderSubjectChanged = true;
    this.form.get('senderDefinition')?.setValue(subject);
    this.form.get('senderDefinition')?.markAsDirty();
  }

  setSenderAddress(address: Nullable<{value: AddressCompleteDto, valid: boolean}>) {
    this.senderSubjectChanged = true;
    this.form.get('senderAddress')?.setValue(address?.value);
    this.form.get('senderAddress')?.markAsDirty();
    if (address?.valid) {
      this.form.get('senderAddressValid')!.setValue(address?.valid);
    } else {
      this.form.get('senderAddressValid')!.setValue(null);
    }
  }

  anonymousSenderSet(anonymousSender: boolean) {
    return anonymousSenderSet(anonymousSender, this.form);
  }

  onSubmit(submitActionType: ObjectDetailSubmitActionType) {
    let request$: Observable<unknown>;
    let updateConsignmentDto: Record<string, any> = {};

    const formValue = this.form.getRawValue();

    const sender = getSender(formValue.senderDefinition);
    const deliveryTime = processTimeControlWithMs(formValue.deliveryTime, this.originalDeliveryTime);

    if (this.isPaperConsignment) {
      updateConsignmentDto = {
        basicServiceId: formValue.basicServiceId,
        deliveryDate: formValue.deliveryDate,
        deliveryTime,
        dispatchDate: formValue.dispatchDate,
        postingNumber: formValue.postingNumber,
        recommended: formValue.recommended,
        senderDefinition: sender.senderDefinition,
        senderId: sender.senderId,
        senderPostOffice: formValue.senderPostOffice,
        addresseeFunctionalPositionId: formValue.addresseeFunctionalPositionId,
        filingOfficeSheetNodeId: formValue.filingOfficeSheetNodeId,
      };

      if (!updateConsignmentDto.recommended) {
        updateConsignmentDto.senderPostOffice = null;
        updateConsignmentDto.postingNumber = null;
      }

      if (this.senderSubjectChanged) {
        updateConsignmentDto.senderDefinition = sender.senderDefinition;
        updateConsignmentDto.senderId = sender.senderId;
        updateConsignmentDto.senderAddress = formValue.senderAddress;
      } else {
        if (isNil(this.form.get('sender')?.value)) {
          updateConsignmentDto.senderDefinition = null;
          updateConsignmentDto.senderAddress = null;
        } else {
          updateConsignmentDto.senderId = sender.senderId;
          updateConsignmentDto.senderAddress = formatAddressForDto(formValue.senderAddressDto as IczFormGroup);
        }
      }

      request$ = this.consignmentWithDuplicateResolveService.receivedPaperConsignmentUpdate(Number(this.receivedPaperConsignment.id), updateConsignmentDto);
    } else if (this.isInternalDelivery) {
      updateConsignmentDto = {
        filingOfficeSheetNodeId: formValue.filingOfficeSheetNodeId,
        addresseeOrgUnitId: formValue.addresseeOrgUnitId,
      };

      // if this.isInternalDelivery is true then it is either paper or digital internal consignment
      const consignmentId = this.receivedPaperInternalConsignment ? this.receivedPaperInternalConsignment.id : this.receivedDigitalInternalConsignment!.id;

      if (this.receivedPaperInternalConsignment) {
        updateConsignmentDto.dispatchDate = formValue.dispatchDate;
        updateConsignmentDto.deliveryDate = formValue.deliveryDate;
        updateConsignmentDto.deliveryTime = deliveryTime;
        request$ = this.consignmentWithDuplicateResolveService.receivedInternalPaperConsignmentUpdate(Number(consignmentId), updateConsignmentDto);
      } else {
        request$ = this.consignmentWithDuplicateResolveService.receivedInternalDigitalConsignmentUpdate(Number(consignmentId), updateConsignmentDto);
      }
    } else {
      const dispatchTime = processTimeControlWithMs(formValue.dispatchTime, this.originalDispatchTime);

      updateConsignmentDto = {...this.receivedDigitalConsignment, dispatchDate: formValue.dispatchDate, dispatchTime};
      if (this.receivedDigitalConsignment.emailAddressDto) {
        const senderEmailAddressValue = this.form.get('senderEmailAddress')!.value;
        if (senderEmailAddressValue) {
          const senderEmailAddressValueSplit = senderEmailAddressValue.split('@');
          updateConsignmentDto.emailAddressDto.senderAddress = senderEmailAddressValue;
          updateConsignmentDto.emailAddressDto.senderLabel = `${senderEmailAddressValue} (${senderEmailAddressValueSplit[0]})`;
        }
      }
      if (this.senderSubjectChanged) {
        updateConsignmentDto.senderDefinition = sender.senderDefinition;
      } else {
        updateConsignmentDto.senderId = sender.senderId;
      }

      request$ = this.consignmentWithDuplicateResolveService.receivedDigitalConsignmentUpdate(Number(this.receivedDigitalConsignment.id), updateConsignmentDto);
    }

    this.loadingService.doLoading(
      request$,
      this.parentComponent!
    ).subscribe(result => {
      if (!result) return;
      this.documentToastService.dispatchConsignmentInfoToast(DocumentToastType.CONSIGNMENT_UPDATED, {
        [InternalNotificationKey.CONSIGNMENT_UID]: this.form.get('uid')!.get('uid')!.value as string,
        [InternalNotificationKey.DOCUMENT_ID]: this.currentDocument.id,
        [InternalNotificationKey.DOCUMENT_SUBJECT]: this.currentDocument.subject,
      });
      this.form.markAsPristine();
      this.documentDetailService.submitFinished$.next();

      if (submitActionType === ObjectDetailSubmitActionType.SAVE_AND_CLOSE) {
        this.historyService.routeBack(createAbsoluteRoute(ApplicationRoute.DOCUMENTS));
      }
      else {
        this.documentDetailService.alreadySaved = true;
        this.wasSenderRemoved = false;
        this.loadConsignmentData();

        this.documentDetailService.reloadObject({
          [ObjectDetailPart.OBJECT_DATA]: load(),
          [DocumentDetailCountType.SUBJECTS]: load(),
        });

        if (!isNil(this.hideSubmitBarTimeout)) {
          clearTimeout(this.hideSubmitBarTimeout);
        }

        this.hideSubmitBarTimeout = setTimeout(() => {
          this.documentDetailService.alreadySaved = false;
        }, 5000) as unknown as number;
      }
    });
  }

  showProtocolDigital() {
    this.plainComponentVersionViewerDialogService.openPlainComponentVersionViewerWithVersionFetch(
      this.receivedDigitalConsignment.protocolDigitalComponentVersionId!,
      undefined,
      this.currentDocument.uid
    ).subscribe();
  }

  showProtocolPersonal() {
    this.plainComponentVersionViewerDialogService.openPlainComponentVersionViewerWithVersionFetch(
      getLastDigitalComponentVersionId(this.personalConsignmentVerificationProtocol!)!
    ).subscribe();
  }

  showOriginal() {
    this.plainComponentVersionViewerDialogService.openPlainComponentVersionViewerWithVersionFetch(
      this.receivedDigitalConsignment.originalDigitalComponentVersionId!,
      this.isDataBoxDelivery ? {mode: PlainComponentViewerMode.DATABOX_CONSIGNMENT_MESSAGE_VIEWER, data: this.receivedDigitalConsignment} : undefined,
      this.currentDocument.uid
    ).subscribe();
  }

  showHeaderDataboxMessage() {
    this.plainComponentVersionViewerDialogService.openPlainComponentVersionViewerWithVersionFetch(
      this.receivedDigitalConsignment.headerDataboxMessageDigitalComponentVersionId!,
      undefined,
      this.currentDocument.uid
    ).subscribe();
  }

  openDocumentComponents() {
    this.router.navigateByUrl(createAbsoluteRoute(
      ApplicationRoute.DOCUMENTS,
      DocumentsRoute.DOCUMENT,
      this.documentDetailService.objectId,
      DocumentDetailRoute.COMPONENTS,
    ));
  }

  verifyDataboxConsignment() {
    this.loadingService.doLoading(
      this.apiReceivedDigitalConsignmentService.receivedDigitalConsignmentVerifyDataboxMessage({
        receivedEpdzMessageId: this.receivedDigitalConsignment.messageId!
      }),
      this
    ).subscribe(result => {
      openDataboxConsignmentValidationDialog(this.dialogService, result);
    });
  }

  protected readonly OrganizationStructureType = OrganizationStructureType;
  protected readonly TreeItemSelectionStrategy = TreeItemSelectionStrategy;
}
